import React, { createContext, useContext, useEffect, ReactNode, useState } from 'react';
import NotificationService from '../services/notificationService';

interface NotificationContextType {
  getCurrentFCMToken: () => Promise<string | null>;
  testNotificationSystem: () => Promise<boolean>;
  isInitialized: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const notificationService = NotificationService.getInstance();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize Firebase messaging
    const initializeNotifications = async () => {
      try {
        const result = await notificationService.initializeFirebaseMessaging();
        setIsInitialized(result);
      } catch (error) {
        setIsInitialized(false);
      }
    };

    // Initialize immediately
    initializeNotifications();

    // Also try to reinitialize after a delay to ensure it works
    const timeoutId = setTimeout(async () => {
      try {
        const result = await notificationService.initializeFirebaseMessaging();
        setIsInitialized(result);
      } catch (error) {
        setIsInitialized(false);
      }
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const getCurrentFCMToken = async () => {
    try {
      return await notificationService.getCurrentFCMToken();
    } catch (error) {
      return null;
    }
  };

  const testNotificationSystem = async () => {
    try {
      return await notificationService.testNotificationSystem();
    } catch (error) {
      return false;
    }
  };

  return (
    <NotificationContext.Provider
      value={{ getCurrentFCMToken, testNotificationSystem, isInitialized }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
